// Search response transformation and validation
const SearchResponseHelper = {
  transformSearchResults(searchData, searchTerm) {
    try {
      this.validateSearchInputs(searchData, searchTerm);

      const products = this.formatSearchProducts(searchData);
      const resultCounts = this.calculateResultCounts({
        collections: searchData.collections,
        blogPosts: searchData.blog_posts,
        ...products,
      });

      return {
        searchTerm,
        collections: searchData.collections || [],
        blogPosts: searchData.blog_posts || [],
        ...products,
        ...resultCounts,
      };
    } catch (error) {
      console.error("Error transforming search results:", error);
      return this.getEmptySearchResults(searchTerm);
    }
  },

  validateSearchInputs(searchData, searchTerm) {
    if (!searchData || typeof searchData !== "object") {
      throw new Error("Invalid search data provided");
    }
    if (!searchTerm || typeof searchTerm !== "string") {
      throw new Error("Invalid search term provided");
    }
  },

  formatProducts(products) {
    return Array.isArray(products)
      ? products.map((product) => ({
          ...product,
          image: {
            url: product.image ? `${product.image}&width=50` : "",
            alt: product.alt_text,
          },
        }))
      : [];
  },

  formatSearchProducts(searchData) {
    return {
      relatedSearch: this.formatProducts(searchData?.related_search || []),
    };
  },

  calculateResultCounts({ collections, blogPosts, relatedSearch }) {
    const collectionsCount = Array.isArray(collections)
      ? collections.length
      : 0;
    const blogPostsCount = Array.isArray(blogPosts) ? blogPosts.length : 0;
    const firstColumnResultsCount = collectionsCount + blogPostsCount;
    const lastColumnResultsCount = relatedSearch.length;

    return {
      collectionsCount,
      blogPostsCount,
      firstColumnResultsCount,
      lastColumnResultsCount,
      relatedSearchCount: relatedSearch.length,
      totalResults: firstColumnResultsCount + lastColumnResultsCount,
      totalSuggestions: collectionsCount,
    };
  },

  getEmptySearchResults(searchTerm) {
    return {
      searchTerm: searchTerm || "",
      collections: [],
      blogPosts: [],
      relatedSearch: [],
      collectionsCount: 0,
      blogPostsCount: 0,
      relatedSearchCount: 0,
      firstColumnResultsCount: 0,
      totalResults: 0,
      totalSuggestions: 0,
    };
  },
};

// Template rendering and Handlebars configuration
const TemplateHelper = {
  getResultsMarkup(data) {
    const source = document.querySelector(
      "#predictive-search-template",
    )?.innerHTML;
    if (!source) return "";
    const template = Handlebars.compile(source);
    return template(data);
  },

  setupHandlebarsHelpers() {
    this.configureHandlebars();
    this.registerTextHelpers();
    this.registerLogicHelpers();
  },

  registerTextHelpers() {
    Handlebars.registerHelper("highlight", function (text, searchWord) {
      const regex = new RegExp(`(${searchWord})`, "gi");
      const highlighted = text.replace(regex, "<strong>$1</strong>");
      return new Handlebars.SafeString(highlighted);
    });

    Handlebars.registerHelper("formatProductTitle", function (title) {
      if (!title) return "";

      const matches = title.match(/^(.*?)\s*\((.*?)\)$/);
      if (!matches)
        return new Handlebars.SafeString(
          `<span style="font-size: 13px; font-weight: 600; line-height: 14.52px;">${title}</span>`,
        );

      const mainTitle = matches[1];
      const subtitle = matches[2];

      return new Handlebars.SafeString(
        `<span style="font-size: 13px; font-weight: 600; line-height: 14.52px;">${mainTitle}</span> ` +
          `<span style="font-size: 13px; font-weight: 400; line-height: 14.52px;">(${subtitle})</span>`,
      );
    });
  },

  registerLogicHelpers() {
    Handlebars.registerHelper("or", function (firstValue, secondValue) {
      return firstValue > 0 || secondValue > 0;
    });

    Handlebars.registerHelper("compare", function (firstValue, secondValue) {
      return firstValue === secondValue;
    });
  },

  configureHandlebars() {
    delimiters(Handlebars, ["\\[\\[", "\\]\\]"]);
  },
};

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
    TemplateHelper.setupHandlebarsHelpers();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]",
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const API_ENDPOINT =
      "https://api-search-x.nudestix.com/stores/nudestixca/search/";
    const searchUrl = `${API_ENDPOINT}?q=${encodeURIComponent(searchTerm)}`;

    fetch(searchUrl, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(
            `Search API error: ${response.status} ${response.statusText}`,
          );
          error.status = response.status;
          this.close();
          throw error;
        }
        return response.json();
      })
      .then((searchResponse) => {
        if (!searchResponse || typeof searchResponse !== "object") {
          throw new Error("Invalid response format from search API");
        }

        const computedResults = SearchResponseHelper.transformSearchResults(
          searchResponse,
          searchTerm,
        );

        const resultsMarkup = TemplateHelper.getResultsMarkup(computedResults);

        // Save bandwidth by keeping the cache in sync across all instances
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return;

        console.error("Predictive search error:", error);
        this.close();

        if (process.env.NODE_ENV === "development") {
          throw error;
        }
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.predictiveSearchResults.style.display = "block";
    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        ?.textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header")?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    // this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    this.predictiveSearchResults.style.display = "block";
    this.adjustLTOImagePadding(this.predictiveSearchResults);
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this.predictiveSearchResults.style.display = "none";
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }

  adjustLTOImagePadding(dropdown) {
    if (dropdown && window.innerWidth >= 1280) {
      const dropdownPaddingTop = 15;
      const dropdownPaddingBottom = 15;
      const ltoCardWidth = 350;
      const totalHeight = dropdown.getBoundingClientRect().height;
      const dropdownHeight =
        totalHeight - dropdownPaddingTop - dropdownPaddingBottom;
      const aspectRatio = dropdownHeight / ltoCardWidth;
      const rawPaddingTop = aspectRatio * 100;
      const paddingTop = Number.isInteger(rawPaddingTop)
        ? rawPaddingTop
        : Number(rawPaddingTop.toFixed(2));

      dropdown.style.setProperty("--LTO-PT", `${paddingTop}%`);
    } else if (dropdown) {
      dropdown.style.setProperty("--LTO-PT", "100%");
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);
